import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import RadioMui, { RadioProps } from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { styled } from '@mui/material/styles'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import DatePicker from '@mui/lab/DatePicker'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import PickersDayMui from '@mui/lab/PickersDay'
import { PickersDayProps } from '@mui/lab/PickersDay/PickersDay'
import isSameDay from 'date-fns/isSameDay'
import isValid from 'date-fns/isValid'
import startOfDay from 'date-fns/startOfDay'

import { AvailableDays } from '../../entities/available-days/AvailableDays'

import messages from './OrdersSelector.messages'

export interface IOwnProps {
    className?: string
}

export interface IStateProps {
    availableDays: null | AvailableDays
    selectedDate: Date
}

export interface IDispatchProps {
    setSelectedDate: (date: Date) => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps

type PickersDayStyledProps = PickersDayProps<Date> & {
    hasOrders?: boolean
}

const PickersDay = styled(PickersDayMui, {
    shouldForwardProp: (prop) => prop !== 'hasOrders',
})<PickersDayStyledProps>(({ theme, hasOrders }) => ({
    '&:focus, &:focus.Mui-selected': {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
    },
    '&.MuiPickersDay-today': {
        borderColor: theme.palette.text.primary,
    },
    ...(hasOrders && {
        '&:focus, &:hover, &': {
            position: 'relative',
            '&::after': {
                content: '""',
                position: 'absolute',
                top: 5,
                right: 5,
                display: 'block',
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: theme.palette.whiteColor.main,
            },
        },
    }),
})) as React.ComponentType<PickersDayStyledProps>

const RadioGroupLabel = styled(FormLabel)(({ theme }) => ({
    fontSize: 13,
    marginRight: theme.spacing(2),
    color: theme.palette.greyColor.light,
}))

const Radio = styled(RadioMui)(({ theme }) => ({
    visibility: 'hidden',
    width: 0,
    height: 0,
    padding: 0,
    '& + .MuiFormControlLabel-label': {
        fontSize: 13,
    },
    '&.Mui-checked': {
        '&, & + .MuiFormControlLabel-label': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
        },
    },
}))

const OrdersSelector: React.FC<IProps> = (props) => {
    const { className, setSelectedDate, availableDays, selectedDate } = props

    // Calculate today and tomorrow dynamically - recalculate on every render
    // This ensures they update when day changes at midnight
    const today = startOfDay(new Date())
    const tomorrow = React.useMemo(() => {
        const tom = startOfDay(new Date())
        tom.setDate(tom.getDate() + 1)
        return tom
    }, [today])

    const [date, setDate] = React.useState<Date>(selectedDate)
    const intl = useIntl()
    const [isPickerOpen, setIsPickerOpen] = React.useState(false)

    // Sync local state with Redux selectedDate prop
    React.useEffect(() => {
        setDate(selectedDate)
    }, [selectedDate])

    const isDayOther = !isSameDay(date, today) && !isSameDay(date, tomorrow)

    const handleDateChange = React.useCallback(
        (date: Date) => {
            setSelectedDate(date)
        },
        [setSelectedDate]
    )

    React.useEffect(() => {
        if (isValid(date)) {
            handleDateChange(date)
        }
    }, [date, handleDateChange])

    const handleOpenPicker = () => {
        setIsPickerOpen(true)
    }

    const handleClosePicker = () => {
        setIsPickerOpen(false)
    }

    const handleChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDate(startOfDay(new Date(event.target.value)))
    }

    const handleChangeOtherDate = (date: Date | null) => {
        setDate(startOfDay(date || new Date()))
    }

    const renderWeekPickerDay = (
        date: Date,
        selectedDates: Array<Date | null>,
        pickersDayProps: PickersDayProps<Date>
    ) => {
        if (!availableDays || !availableDays.hasOrders(date)) {
            return <PickersDay {...pickersDayProps} disabled={true} />
        }

        return <PickersDay {...pickersDayProps} hasOrders={true} />
    }

    return (
        <FormControl className={className}>
            <RadioGroupLabel id='orders-selector'>
                <FormattedMessage {...messages.title} />
            </RadioGroupLabel>
            <RadioGroup
                row
                aria-labelledby='orders-selector'
                name='date'
                value={date.toString()}
                onChange={handleChangeDate}
            >
                {
                    <FormControlLabel
                        value={today.toString()}
                        control={<Radio />}
                        label={
                            <OrdersRadioLabel
                                label={intl.formatMessage({
                                    ...messages.today,
                                })}
                                number={availableDays?.getOrdersCountByDate(
                                    today
                                )}
                            />
                        }
                    />
                }

                {availableDays?.getOrdersCountByDate(tomorrow) && (
                    <FormControlLabel
                        value={tomorrow.toString()}
                        control={<Radio />}
                        label={
                            <OrdersRadioLabel
                                label={intl.formatMessage({
                                    ...messages.tomorrow,
                                })}
                                number={availableDays?.getOrdersCountByDate(
                                    tomorrow
                                )}
                            />
                        }
                    />
                )}
                {availableDays?.getOrdersCountForDaysAfterTomorrow() && (
                    <FormControlLabel
                        value={date.toString()}
                        control={
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={intl.formatMessage({
                                        ...messages.later,
                                    })}
                                    desktopModeMediaQuery='@media (min-width: 320px)'
                                    value={date}
                                    open={isPickerOpen}
                                    onChange={(newValue) => {
                                        handleChangeOtherDate(newValue)
                                        handleClosePicker()
                                    }}
                                    minDate={today}
                                    renderDay={renderWeekPickerDay}
                                    renderInput={(params) => {
                                        const {
                                            inputProps,
                                            InputProps,
                                            error,
                                            ...rest
                                        } = params
                                        return (
                                            <Radio
                                                {...{
                                                    ...(rest as RadioProps),
                                                    checked: isDayOther,
                                                }}
                                                onClick={handleOpenPicker}
                                            />
                                        )
                                    }}
                                />
                            </LocalizationProvider>
                        }
                        label={
                            <OrdersRadioLabel
                                label={intl.formatMessage({
                                    ...messages.later,
                                })}
                                number={availableDays?.getOrdersCountForDaysAfterTomorrow()}
                            />
                        }
                    />
                )}
            </RadioGroup>
        </FormControl>
    )
}

const Label = styled('span')(({ theme }) => ({
    display: 'inline-block',
    fontSize: 12,
    lineHeight: 1,
    textAlign: 'center',
    padding: '5px 4px',
    border: '1px solid currentColor',
    borderRadius: 20,
    minWidth: 24,
    marginLeft: theme.spacing(2),
    boxSizing: 'border-box',
}))
export interface IOrdersRadioLabelOwnProps {
    label: string | React.ReactElement
    number?: number
}

export type IOrdersRadioLabelProps = IOrdersRadioLabelOwnProps

export const OrdersRadioLabel: React.FC<IOrdersRadioLabelProps> = (props) => {
    const { label, number = 0 } = props

    return (
        <>
            {label}
            <Label>{number >= 100 ? <>99+</> : <>{number}</>}</Label>
        </>
    )
}

export default styled(OrdersSelector)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
}))
